import { useMetaGetPostCreationEntitiesQuery, useMetastartOAuthQuery, useMetaDisconnectMutation } from "@/lib/api/requests/integrations.requests";
import { useWhatsappGroupsQuery, useWhatsappCreateActivationCodeMutation, useWhatsappUpdateGroupMutation } from "@/lib/api/requests/whatsapp.requests";
import { Button } from "@/ui-components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/ui-components/ui/card";
import { Loader2, Check, User, Share2, MessageCircle, MoreHorizontal, RefreshCcw, Unlink, HelpCircle, Smartphone, AlertCircle, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "@/lib/toast";
import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { SectionHeader } from "@/util-components/page-layout/section-header";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { withTelemetrySpan } from "@/lib/monitoring/telemetry";
import { Context } from "@/util-components/root/context";
import { SentrySectionBoundary } from "@/util-components/error/sentry-section-boundary";
import { cn } from "@/lib/utils/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/util-components/dropdown-menu";

const MetaIntegrationCardSkeleton = () => (
  <Card className="overflow-hidden border-gray-200 shadow-sm">
    <CardHeader className="border-b border-gray-100 bg-gray-50/30 py-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
    </CardHeader>
    <CardContent className="p-6 space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
      </div>
      <Skeleton className="h-10 w-full rounded-xl" />
    </CardContent>
  </Card>
);

const WhatsappIntegrationCardSkeleton = () => (
  <Card className="overflow-hidden border-gray-200 shadow-sm">
    <CardHeader className="border-b border-gray-100 bg-gray-50/30 py-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-36" />
          </div>
        </div>
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
    </CardHeader>
    <CardContent className="p-6 space-y-4">
      <div className="grid gap-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-8 w-full rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-10 w-full rounded-xl" />
    </CardContent>
  </Card>
);

const Integrations = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const error = searchParams.get("error");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Synchronized loading for both integrations
  const { data: metaData, isLoading: isLoadingMeta } = useMetaGetPostCreationEntitiesQuery();
  const { data: whatsappData, isLoading: isLoadingWhatsapp } = useWhatsappGroupsQuery();

  const isInitialLoading = isLoadingMeta || isLoadingWhatsapp;

  useEffect(() => {
    if (error) {
      let decodedError = error;
      try {
        decodedError = decodeURIComponent(error);
      } catch (e) {
        console.error("Failed to decode error param", e);
      }
      
      setErrorMessage(decodedError);
      toast.error(decodedError);
      
      // Remove the error param from the URL
      setSearchParams((prev: URLSearchParams) => {
        prev.delete("error");
        return prev;
      });
    }
  }, [error, setSearchParams]);

  return (
    <SubPageLayout
      pageTitle="Integrationen"
      parentPageTitle="Dein Verein"
      parentPageCallback={() => navigate("/vereine")}
      navigateBackCallback={() => navigate("/vereine")}
      icon={<Share2 className="h-6 w-6" />}
    >
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
        <div className="space-y-12">
            <SectionHeader
              icon={Smartphone}
              title="Social Media & Messenger"
              description="Verbinde deine Konten, um erstellte Grafiken direkt und automatisch zu teilen."
            />

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {isInitialLoading ? (
              <>
                <MetaIntegrationCardSkeleton />
                <WhatsappIntegrationCardSkeleton />
              </>
            ) : (
              <>
                <SentrySectionBoundary
                  boundaryKey="integrations-meta-card"
                  title="Meta-Integration konnte nicht geladen werden"
                  description="Dieser Bereich ist abgestürzt. Du kannst die Seite neu laden und es erneut versuchen."
                  className="min-h-[320px] rounded-3xl border border-gray-100 bg-white p-6"
                >
                  <MetaIntegrationCard 
                    errorMessage={errorMessage} 
                    initialData={metaData}
                  />
                </SentrySectionBoundary>
                
                <SentrySectionBoundary
                  boundaryKey="integrations-whatsapp-card"
                  title="WhatsApp-Integration konnte nicht geladen werden"
                  description="Dieser Bereich ist abgestürzt. Du kannst die Seite neu laden und es erneut versuchen."
                  className="min-h-[320px] rounded-3xl border border-gray-100 bg-white p-6"
                >
                  <WhatsappIntegrationCard 
                    initialData={whatsappData}
                  />
                </SentrySectionBoundary>
              </>
            )}
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
};

const MetaIntegrationCard = ({ errorMessage, initialData }: { errorMessage: string | null; initialData: any }) => {
  const { data: entities } = useMetaGetPostCreationEntitiesQuery({ initialData });
  const connectedAccounts = Array.isArray(entities?.accounts) ? entities.accounts : [];
  const isConnected = connectedAccounts.length > 0;
  const tokenStatus = entities?.tokenStatus ?? 'not_connected';
  const isExpired = tokenStatus === 'expired';
  const isExpiringSoon = tokenStatus === 'expiring_soon';
  const [rerequest, setRerequest] = useState(false);

  useEffect(() => {
    if (errorMessage?.includes("Missing permissions")) {
      setRerequest(true);
    }
  }, [errorMessage]);

  const { refetch: startOAuth, isFetching, isError } = useMetastartOAuthQuery({ rerequest }, {
    enabled: false,
    retry: false,
  });

  const { mutate: disconnect, isPending: isDisconnecting } = useMetaDisconnectMutation({
    onSuccess: () => {
      toast.success("Verbindung erfolgreich getrennt");
      setRerequest(false);
    },
    onError: (error) => toast.error("Verbindung konnte nicht getrennt werden", { error })
  });

  const handleConnect = async () => {
    try {
      await withTelemetrySpan("integration.meta.connect_start", "feature.integration.meta.connect", {
        "integration.provider": "meta",
        "integration.rerequest": rerequest,
        "integration.already_connected": isConnected,
      }, async () => {
        const result = await startOAuth();
        if (result.data?.url) {
          window.location.href = result.data.url;
        } else {
          toast.error("Keine Redirect-URL erhalten");
        }
      });
    } catch (error) {
      toast.error("Verbindung konnte nicht gestartet werden", { error });
    }
  };

  return (
    <Card className="flex flex-col overflow-hidden border-gray-200 shadow-sm transition-all hover:shadow-md h-full">
      <CardHeader className="border-b border-gray-100 bg-gray-50/30 py-5 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-600 font-bold text-lg shadow-sm">
              f
            </div>
            <div className="space-y-0.5">
              <CardTitle className="text-sm font-bold text-gray-900">Meta</CardTitle>
              <CardDescription className="text-xs text-gray-500">Facebook & Instagram</CardDescription>
            </div>
          </div>
          
          {isConnected && (
            <div className="flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-2.5 py-1 text-[10px] font-bold text-green-700 shadow-sm">
              <Check className="h-3 w-3" />
              AKTIV
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-6 flex-1 flex flex-col">
        <div className="flex-1 space-y-5">
          {errorMessage && (
            <div className="rounded-xl border border-red-100 bg-red-50/50 p-3 text-xs text-red-600">
              <p className="font-bold mb-1">Fehler</p>
              {errorMessage}
            </div>
          )}

          {/* Token-Status Hinweise */}
          {isExpired && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 flex items-start gap-2.5">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-bold text-red-800 mb-0.5">Verbindung abgelaufen</p>
                <p className="text-xs text-red-700 leading-relaxed">
                  Dein Zugriffstoken ist abgelaufen. Bitte verbinde dein Konto erneut, damit das Posten wieder funktioniert.
                </p>
              </div>
            </div>
          )}
          {isExpiringSoon && !isExpired && (
            <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-3 flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-bold text-yellow-800 mb-0.5">Verbindung läuft bald ab</p>
                <p className="text-xs text-yellow-700 leading-relaxed">
                  Dein Zugriffstoken läuft in weniger als 7 Tagen ab. Klicke auf „Aktualisieren", um ihn zu erneuern.
                </p>
              </div>
            </div>
          )}

          {!isConnected ? (
            <p className="text-xs leading-relaxed text-gray-500">
              Verbinde dein Facebook-Konto, um Posts automatisch auf deinen Seiten und in Instagram Stories zu teilen.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  <span>Verbundene Seiten</span>
                  <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-gray-600">{connectedAccounts.length}</span>
                </div>
                
                <div className="max-h-[160px] space-y-2 overflow-y-auto pr-1 custom-scrollbar">
                  {connectedAccounts.map(acc => (
                    <div key={acc.id} className="flex items-center justify-between rounded-xl border border-gray-100 bg-white p-2.5 transition-all hover:border-blue-100 hover:shadow-sm">
                      <div className="flex items-center gap-2.5 overflow-hidden">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400 border border-gray-100">
                          <User className="h-3.5 w-3.5" />
                        </div>
                        <span className="truncate text-xs font-bold text-gray-700">{acc.name}</span>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <span className="rounded-md border border-blue-100 bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold text-blue-700">FB</span>
                        {acc.instagramAllowed && (
                          <span className="rounded-md border border-pink-100 bg-pink-50 px-1.5 py-0.5 text-[9px] font-bold text-pink-700">IG</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="pt-5 mt-auto">
          <div className="flex gap-2">
            {!isConnected || isExpired ? (
              <Button
                onClick={handleConnect}
                disabled={isFetching}
                className={`h-10 w-full rounded-xl font-bold text-white shadow-md transition-all active:scale-[0.98] ${isExpired ? 'bg-red-600 hover:bg-red-700 shadow-red-500/20' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/20'}`}
              >
                {isFetching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isExpired ? (
                  <><RefreshCw className="mr-2 h-3.5 w-3.5" />Jetzt neu verbinden</>
                ) : rerequest ? (
                  "Berechtigungen anfordern"
                ) : (
                  "Mit Meta verbinden"
                )}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleConnect}
                  variant="outline"
                  className="h-10 flex-1 rounded-xl border-gray-200 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50 active:scale-[0.98]"
                >
                  <RefreshCcw className="mr-2 h-3.5 w-3.5" />
                  Aktualisieren
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="h-10 w-10 rounded-xl border-gray-200 p-0 text-gray-400 transition-all hover:bg-gray-50 active:scale-[0.98]">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40 rounded-xl">
                    <DropdownMenuItem 
                      onClick={() => disconnect()} 
                      disabled={isDisconnecting}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                    >
                      <Unlink className="mr-2 h-4 w-4" />
                      Trennen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>

          {isError && (
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-red-100 bg-red-50/50 p-3 text-[11px] text-red-600">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
              Fehler beim Verbinden.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const WhatsappIntegrationCard = ({ initialData }: { initialData: any }) => {
  const { data } = useWhatsappGroupsQuery({ initialData });
  const groups = data?.groups ?? [];
  const botPhoneNumber = data?.botPhoneNumber;
  
  const { mutateAsync: createCode, isPending: isCreating } = useWhatsappCreateActivationCodeMutation();
  const { mutate: updateGroup, isPending: isUpdating } = useWhatsappUpdateGroupMutation();
  const { refetchUser } = useContext(Context);
  const previousHasGroupsRef = useRef<boolean>(false);
  const [lastCode, setLastCode] = useState<{ activationCode: string; expiresAt: string; botPhoneNumber?: string; } | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  const handleCreateCode = async () => {
    await withTelemetrySpan("integration.whatsapp.activation_code.create", "feature.integration.whatsapp.connect", {
      "integration.provider": "whatsapp",
      "integration.existing_group_count": groups.length,
    }, async () => {
      const result = await createCode({});
      setLastCode(result);
    });
  };

  const hasGroups = groups.length > 0;

  useEffect(() => {
    if (hasGroups && !previousHasGroupsRef.current) {
      void refetchUser?.();
    }
    previousHasGroupsRef.current = hasGroups;
  }, [hasGroups, refetchUser]);

  const Instructions = () => (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-gray-500">
        Verbinde deine Vereins-Gruppe, um Grafiken automatisch per WhatsApp zu teilen.
      </p>
      
      <div className="space-y-2 rounded-2xl border border-gray-100 bg-gray-50/50 p-3.5">
        {[
          "Klicke auf Verbinden",
          botPhoneNumber ? `Speichere ${botPhoneNumber} als Kontakt` : "Speichere Kontakt",
          "Füge Gruppe hinzu",
          "Sende /activate CODE als Nachricht in die Gruppe"
        ].map((step, i) => (
          <div key={i} className="flex items-center gap-3 text-[11px] text-gray-600 font-medium">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-[10px] font-bold text-gray-900 shadow-sm">
              {i + 1}
            </div>
            <span>{step}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Card className="flex flex-col overflow-hidden border-gray-200 shadow-sm transition-all hover:shadow-md h-full">
      <CardHeader className="border-b border-gray-100 bg-gray-50/30 py-5 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-green-100 bg-green-50 text-green-600 shadow-sm">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div className="space-y-0.5">
              <CardTitle className="text-sm font-bold text-gray-900">WhatsApp</CardTitle>
              <CardDescription className="text-xs text-gray-500">Gruppen-Integration</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasGroups && (
              <>
                <button 
                  onClick={() => setShowHelp(!showHelp)}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full transition-all hover:bg-gray-100",
                    showHelp ? "text-blue-600 bg-blue-50" : "text-gray-400"
                  )}
                  title="Anleitung anzeigen"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-2.5 py-1 text-[10px] font-bold text-green-700 shadow-sm">
                  <Check className="h-3 w-3" />
                  AKTIV
                </div>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6 flex-1 flex flex-col">
        <div className="flex-1 space-y-5">
          {(!hasGroups || showHelp) && <Instructions />}

          {hasGroups && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-gray-400">
                <span>Verbundene Gruppen</span>
                <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-gray-600">{groups.length}</span>
              </div>

              <div className="max-h-[160px] space-y-2 overflow-y-auto pr-1 custom-scrollbar">
                {groups.map((group) => (
                  <div key={group.id} className="flex items-center justify-between rounded-xl border border-gray-100 bg-white p-2.5 transition-all hover:border-blue-100">
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-xs font-bold text-gray-700">{group.displayName || 'Gruppe'}</span>
                      <span className="truncate text-[10px] text-gray-400">{group.whatsappChatId}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        "rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-tight",
                        group.status === 'active' ? "border-green-100 bg-green-50 text-green-700" : "border-gray-100 bg-gray-50 text-gray-500"
                      )}>
                        {group.status === 'active' ? 'AKTIV' : 'AUS'}
                      </span>
                      <button
                        onClick={() => updateGroup({ id: group.id, status: group.status === 'active' ? 'disabled' : 'active' })}
                        disabled={isUpdating}
                        className="text-[11px] font-bold text-blue-600 hover:text-blue-700 active:scale-95 transition-all"
                      >
                        {group.status === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="pt-5 mt-auto">
          <div className="space-y-3">
            <Button
              onClick={handleCreateCode}
              disabled={isCreating}
              className="h-10 w-full rounded-xl bg-green-600 font-bold text-white shadow-md shadow-green-500/20 transition-all hover:bg-green-700 active:scale-[0.98]"
            >
              {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : (hasGroups ? "Weitere Gruppe verbinden" : "WhatsApp verbinden")}
            </Button>

            {lastCode && (
              <div className="rounded-xl border border-green-100 bg-green-50/50 p-3 text-[11px] text-green-800 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex items-center justify-between">
                  <span className="font-bold opacity-70">Code</span>
                  <span className="font-mono text-xs font-bold bg-white px-2 py-0.5 rounded border border-green-100">{lastCode.activationCode}</span>
                </div>
                {lastCode.botPhoneNumber && (
                  <div className="mt-2 flex items-center justify-between border-t border-green-100/50 pt-2">
                    <span className="font-bold opacity-70">Nummer</span>
                    <span className="font-mono text-xs font-bold">{lastCode.botPhoneNumber}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Integrations;
